/**
 * Brand Resolver
 *
 * Resolves which brand config to use based on hostname or environment variable.
 * Used by both the worker (server-side) and BrandProvider (client-side).
 */

import pentatonicConfig from "./pentatonic.json";
import adidasConfig from "./adidas.json";

// All brand configs indexed by slug
const BRAND_CONFIGS = {
  pentatonic: pentatonicConfig,
  adidas: adidasConfig,
};

// Build hostname → slug mapping from brand configs
const HOSTNAME_MAP = {};
for (const config of Object.values(BRAND_CONFIGS)) {
  for (const hostname of config.hostnames || []) {
    HOSTNAME_MAP[hostname] = config.slug;
  }
}

/**
 * Resolve brand slug from hostname or explicit slug
 * @param {string} [hostname] - Request hostname (e.g. "marketplace.pentatonic.com")
 * @param {string} [envSlug] - Explicit brand slug from env (e.g. "adidas")
 * @returns {string} Brand slug
 */
export function resolveBrandSlug(hostname, envSlug) {
  // Explicit env slug takes priority
  if (envSlug) return envSlug;

  // Try hostname lookup (strip port for localhost)
  if (hostname) {
    const host = hostname.split(":")[0];
    if (HOSTNAME_MAP[host]) return HOSTNAME_MAP[host];
  }

  // Default to pentatonic
  return "pentatonic";
}

/**
 * Get full brand config by slug
 * @param {string} slug - Brand slug
 * @returns {object} Brand config
 */
export function getBrandConfig(slug) {
  return BRAND_CONFIGS[slug] || BRAND_CONFIGS.pentatonic;
}

/**
 * Resolve full brand config from hostname or env slug
 * @param {string} [hostname]
 * @param {string} [envSlug]
 * @returns {object} Brand config
 */
export function resolveBrand(hostname, envSlug) {
  const slug = resolveBrandSlug(hostname, envSlug);
  return getBrandConfig(slug);
}

export { BRAND_CONFIGS };
